import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import { useExperiment, useStatsigClient } from "@statsig/react-bindings";
import {
  IStructuredHeaderLinksData,
  PureHeaderLinks,
} from "./pure-header-links";
import { PureHeaderLinksLegacy } from "./pure-header-links-legacy";
import { STATSIG_EXPERIMENTS } from "../../utils/statsig/experiments";
import { STATSIG_SDK_KEY } from "../../utils/statsig/statsig.constants";
import { StatsigIdentityContext } from "../../utils/statsig/statsig-provider";
import { isCrawler } from "../../utils/isCrawler";
import { useMergePrismicPreviewData } from "gatsby-plugin-prismic-previews";
import { NavRevealWrapper } from "./header-links.styled";

// Statsig experiment parameter (must match the console definition of
// STATSIG_EXPERIMENTS.globalNavV2, case-sensitive): true => new nav,
// default/control => legacy.
const NEW_NAV_PARAM = "Enabled";

// Whether Statsig is actually running for this visitor. Mirrors the gating in
// statsig-provider.tsx (StatsigWrapper): crawlers and key-less envs get NO
// provider, so the experiment hook falls back to the Noop client (control) and
// there is nothing to wait for -> render control immediately, visible (keeps
// nav links in the HTML for SEO). Cached once, like the provider's isBot.
const STATSIG_ACTIVE = !isCrawler() && !!STATSIG_SDK_KEY;

// Safety net: reveal the nav regardless if Statsig hasn't resolved within this
// window, so a stalled/failed init can never leave the nav permanently hidden.
const REVEAL_TIMEOUT_MS = 1400;

// Evaluation reasons that mean "no real bucket yet" (see @statsig EvaluationStore
// source strings). Anything else — Cache:*, Network:*, NetworkNotModified:*,
// Bootstrap:* — is a resolved bucket we can safely reveal.
function isResolvedReason(reason: string): boolean {
  return (
    reason.length > 0 &&
    !reason.startsWith("Uninitialized") &&
    !reason.startsWith("Loading") &&
    !reason.startsWith("NoValues")
  );
}

// The proposed nav is a different Prismic document (the condensed 3-item layout:
// Solutions | Industries | Become a Supplier), not just a re-render of the
// current 6-item nav. Treatment swaps the nav content to this doc.
const NEW_NAV_UID = "condensed-links";

// Local testing override: `?nav=v2` (new) or `?nav=legacy` (old). Bypasses
// Statsig so both arms are viewable locally even without a Statsig key set.
// Returns null when no override is present (fall back to the experiment).
function getNavOverride(): boolean | null {
  if (typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get("nav");
  if (value === "v2" || value === "new") return true;
  if (value === "legacy" || value === "old") return false;
  return null;
}

export interface NavHold {
  // Hold = render but keep hidden (visibility:hidden, space reserved) until the
  // bucket resolves, so no variant is painted before Statsig decides.
  held: boolean;
  useNewNav: boolean;
}

export type NavHoldDecisionInput = {
  navExperimentEligible: boolean;
  mounted: boolean;
  override: boolean | null;
  statsigActive: boolean;
  timedOut: boolean;
  resolved: boolean;
  identityReady: boolean;
  experimentEnabled: boolean;
};

// Pure decision tree for nav hold/variant. Exported for unit tests.
export function resolveNavHoldDecision(input: NavHoldDecisionInput): NavHold {
  const {
    navExperimentEligible,
    mounted,
    override,
    statsigActive,
    timedOut,
    resolved,
    identityReady,
    experimentEnabled,
  } = input;

  if (!mounted) {
    if (!navExperimentEligible) {
      return { held: false, useNewNav: false };
    }
    return { held: true, useNewNav: false };
  }
  if (override !== null) {
    return { held: false, useNewNav: override };
  }
  if (!navExperimentEligible) {
    return { held: false, useNewNav: false };
  }
  if (!statsigActive) {
    return { held: false, useNewNav: false };
  }
  if (timedOut) {
    return { held: false, useNewNav: experimentEnabled };
  }
  if (resolved && identityReady) {
    return { held: false, useNewNav: experimentEnabled };
  }
  return { held: true, useNewNav: false };
}

// Single source of truth for the global_nav_v2 hold/variant decision. Called
// ONCE by the header so the nav AND the adjacent search toggle hold and reveal
// together off one evaluation — a second useExperiment call would risk a
// duplicate exposure. Gate all client-only reads behind `mounted` so the first
// client render is byte-identical to SSR (control markup, held) — no hydration
// mismatch. On mount the experiment/override/hold logic engages.
export function useNavHold(navExperimentEligible = true): NavHold {
  // Exposure is logged manually below, not by the hook: useExperiment auto-logs
  // on first eval — for a cold client that's the NoValues/Loading window, and the
  // SDK dedupes per session, so that premature exposure would suppress the real
  // resolved-bucket one.
  const experiment = useExperiment(STATSIG_EXPERIMENTS.globalNavV2, {
    disableExposureLog: true,
  });
  const { client } = useStatsigClient();
  // Identity gate: for a logging-in visitor this stays false until the userID
  // bucket has been applied, so we never reveal the transient anonymous bucket
  // and then flip to the userID bucket (the measured warm + logged-in thrash).
  const { identityReady } = React.useContext(StatsigIdentityContext);
  const [mounted, setMounted] = React.useState(false);
  const [timedOut, setTimedOut] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  React.useEffect(() => {
    if (!STATSIG_ACTIVE) return undefined;
    const id = window.setTimeout(() => setTimedOut(true), REVEAL_TIMEOUT_MS);
    return () => window.clearTimeout(id);
  }, []);

  // ?nav=v2|legacy is a dev/QA override that forces a variant immediately and
  // bypasses the hold. Only read post-mount (it touches window) to stay
  // hydration-safe; forcing it one tick after mount still counts as immediate.
  const override = mounted ? getNavOverride() : null;
  const resolved = isResolvedReason(experiment.details?.reason ?? "");

  // One exposure, via getExperiment, only once we reveal the resolved bucket for
  // the final identity — not during the hold, and not for the ?nav= override
  // (a forced view, not an assignment).
  const exposedRef = React.useRef(false);
  React.useEffect(() => {
    if (
      !STATSIG_ACTIVE ||
      !mounted ||
      !navExperimentEligible ||
      exposedRef.current
    ) {
      return;
    }
    if (override !== null) return;
    if (resolved && identityReady) {
      exposedRef.current = true;
      client.getExperiment(STATSIG_EXPERIMENTS.globalNavV2);
    }
  }, [
    client,
    mounted,
    navExperimentEligible,
    override,
    resolved,
    identityReady,
  ]);

  return resolveNavHoldDecision({
    navExperimentEligible,
    mounted,
    override,
    statsigActive: STATSIG_ACTIVE,
    timedOut,
    resolved,
    identityReady,
    experimentEnabled: experiment.get(NEW_NAV_PARAM, false),
  });
}

interface IHeaderLinksProps {
  headerLinksUID: string;
  // Hold + variant decision, computed once by the header via useNavHold so the
  // nav and the search toggle stay in lockstep.
  held: boolean;
  useNewNav: boolean;
  setHeaderLinksData: (data: IStructuredHeaderLinksData) => void;
  isHeaderLinksDataSet: boolean;
  isOpen: boolean;
  showAnnouncementBanner: boolean;
  isDarkMode?: boolean;
  setIsDesktopNavOpen: (isOpen: boolean) => void;
  registerEscapeHandler?: (handler: (() => boolean) | null) => void;
  registerNavCloseHandler?: (handler: (() => void) | null) => void;
  onRequestCloseMobileNav?: () => void;
}

const query = graphql`
  query HeaderLinks {
    allPrismicHeaderLinks {
      edges {
        node {
          ...HeaderLinksNode
        }
      }
    }
  }
`;

// Render function for Header Links. Queries Prismic in a static query and renders
// either the new or legacy nav based on the held/variant decision passed in from
// the header (useNavHold).
export default function RenderHeaderLinks(props: IHeaderLinksProps) {
  const staticData = useStaticQuery(query);
  const headerLinks = useMergePrismicPreviewData(staticData);

  const { held, useNewNav } = props;

  // Treatment shows the proposed 3-item nav content; control keeps the page's
  // normal doc (default-links on most pages). Pages with "no-links" never reach
  // this component (header.tsx hides the nav), so they're unaffected.
  const effectiveUID = useNewNav ? NEW_NAV_UID : props.headerLinksUID;

  const commonProps = {
    headerLinks,
    headerLinksUID: effectiveUID,
    setHeaderLinksData: props.setHeaderLinksData,
    isHeaderLinksDataSet: props.isHeaderLinksDataSet,
    isOpen: props.isOpen,
    showAnnouncementBanner: props.showAnnouncementBanner,
    isDarkMode: props.isDarkMode,
    setIsDesktopNavOpen: props.setIsDesktopNavOpen,
    registerEscapeHandler: props.registerEscapeHandler,
    registerNavCloseHandler: props.registerNavCloseHandler,
    onRequestCloseMobileNav: props.onRequestCloseMobileNav,
  };

  return (
    <NavRevealWrapper $held={held}>
      {useNewNav ? (
        <PureHeaderLinks {...commonProps} />
      ) : (
        <PureHeaderLinksLegacy {...commonProps} />
      )}
    </NavRevealWrapper>
  );
}
